import { useState } from "react";
import { useLocationsDeleteConfirm } from "./useLocationsDeleteConfirm";
import { useLocationsDefaultLocationNotice } from "./useLocationsDefaultLocationNotice";
import type { UUID } from "@/Shared/types";
import type { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { FormBox } from "@freightsimple/ui-core/FormLayout/FormBox";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import { useSavedLocationsService } from "@/Shared/Locations/Services/LocationsSavedService";
import { useUserService } from "@/Services/UserService";
import YellowExclamationAnimatedIcon from "@/Shared/Animations/YellowExclamationAnimatedIcon";

export const useLocationsOperations = () => {
  const savedLocationsService = useSavedLocationsService();
  const savedLocationsApi = useSavedLocationsApi();
  const userService = useUserService();

  const {
    askDeleteConfirm,
    confirmDelete,
    dialogs: deleteConfirmDialogs,
  } = useLocationsDeleteConfirm();
  const { showDefaultLocationNotice, dialogs: defaultLocationNoticeDialogs } =
    useLocationsDefaultLocationNotice();

  const [loadingLocations, setLoadingLocations] = useState<
    Record<string, boolean>
  >({});

  /**
   * This function wraps an API call that modifies a single location in
   * an optimistic update.
   * The function takes 6 parameters:
   * - locationId: the id of the location to be modified
   * - optimisticUpdate: the function that implements the optimistic update
   * - apiCall: the API call that modifies the location
   * - refreshData: a function that retrieves the modified location from the API
   * - revertUpdate: a function that reverts the optimistic update in case of errors
   * - errorMessage: a string that is prepended to the error message if the API call fails
   *
   * The function first calls the optimisticUpdate function updating the frontend,
   * then calls the apiCall and refreshData functions to silently update the local
   * state with the new data.
   * If the API call fails, it calls the revertUpdate function to undo the optimistic
   * update and throws an error.
   * Finally, it sets the loading state of the location to false.
   *
   * The function returns the modified location.
   *
   * notes: onDelete, onToggleDefaultPickupLocation, onToggleDefaultDeliveryLocation
   * work in a similar structure, but they are not wrapped in this function since
   * they require to get all the locations after updating the server data
   */
  const handleLocationUpdate = async (
    locationId: UUID,
    optimisticUpdate: () => void,
    apiCall: () => Promise<void>,
    refreshData: () => Promise<SavedLocation>,
    revertUpdate: () => void,
    errorMessage: string,
  ) => {
    optimisticUpdate();
    setLoadingLocations((prev) => ({ ...prev, [locationId]: true }));

    try {
      await apiCall();
      const freshData = await refreshData();
      return freshData;
    } catch (error) {
      revertUpdate();
      throw new Error(errorMessage + error);
    } finally {
      setLoadingLocations((prev) => ({ ...prev, [locationId]: false }));
    }
  };

  const onModifyLocation = async (
    locationId: UUID,
    updatedLocation: Location,
    setLocations: (
      updateFn: (
        prev: SavedLocation[] | undefined,
      ) => SavedLocation[] | undefined,
    ) => void,
    locationHasDock?: LoadingCapabilitiesAvailability,
    locationHasForklift?: LoadingCapabilitiesAvailability,
  ) => {
    return handleLocationUpdate(
      locationId,
      () => {
        setLocations((prevLocations) =>
          prevLocations?.map((loc) =>
            loc.savedLocationId === locationId
              ? {
                  ...loc,
                  location: updatedLocation,
                  ...(locationHasDock !== undefined ? { locationHasDock } : {}),
                  ...(locationHasForklift !== undefined
                    ? { locationHasForklift }
                    : {}),
                }
              : loc,
          ),
        );
      },
      () =>
        savedLocationsService.modifyLocation(
          locationId,
          updatedLocation,
          locationHasDock,
          locationHasForklift,
        ),
      () => savedLocationsService.getLocation(locationId),
      () => {
        setLocations((prevLocations) =>
          prevLocations?.map((loc) =>
            loc.savedLocationId === locationId
              ? { ...loc, location: loc.location }
              : loc,
          ),
        );
      },
      "Failed to update location: ",
    );
  };

  const onDelete = async (
    savedLocationId: UUID,
    savedLocation: SavedLocation,
    setLocations: (
      updateFn: (
        prev: SavedLocation[] | undefined,
      ) => SavedLocation[] | undefined,
    ) => void,
    setDefaultPickupLocationId: (id: string | undefined) => void,
    setDefaultDeliveryLocationId: (id: string | undefined) => void,
  ) => {
    let confirmed = false;

    // If it's a branch, check for assigned users
    if (savedLocation.isBranch) {
      try {
        const users = await userService.listUsersForCompany();
        const assignedUsers = users.filter(
          (user) => user.branchId === savedLocationId,
        );

        if (assignedUsers.length > 0) {
          // Create message with user list
          const message = (
            <Stack gap={4}>
              <Row gap={2}>
                <YellowExclamationAnimatedIcon />
                <T size="base" color="secondary">
                  You are about to delete a branch that has{" "}
                  <strong>{assignedUsers.length}</strong> user
                  {assignedUsers.length > 1 ? "s" : ""} assigned to it.
                </T>
              </Row>

              <Stack gap={2}>
                <T size="base" color="secondary">
                  These users will stop being assigned to a branch:
                </T>
                <FormBox variant="filled">
                  <Stack gap={1}>
                    {assignedUsers.map((user) => (
                      <T key={user.email} mono size="sm">
                        • {user.name} ({user.email})
                      </T>
                    ))}
                  </Stack>
                </FormBox>
              </Stack>
              <T size="base" color="secondary">
                Are you sure you want to continue?
              </T>
            </Stack>
          );

          confirmed = await askDeleteConfirm("Confirm delete branch", message);
        } else {
          // Branch with no users, just use regular confirm
          confirmed = await confirmDelete();
        }
      } catch (error) {
        console.error("Error fetching users for branch:", error);
        // Fallback to regular confirm if we can't fetch users
        confirmed = await confirmDelete();
      }
    } else {
      // Not a branch, use regular confirm
      confirmed = await confirmDelete();
    }

    if (confirmed) {
      setLocations((prevLocations) =>
        prevLocations?.filter((loc) => loc.savedLocationId !== savedLocationId),
      );

      try {
        await savedLocationsService.deleteSavedLocation(savedLocationId);
        const savedLocationsResponse =
          await savedLocationsApi.getAllSavedLocations();
        setLocations(() =>
          savedLocationsResponse.items.sort((a, b) => {
            return a.location.businessName.localeCompare(
              b.location.businessName,
            );
          }),
        );
        setDefaultPickupLocationId(
          savedLocationsResponse.defaultPickupLocationId,
        );
        setDefaultDeliveryLocationId(
          savedLocationsResponse.defaultDeliveryLocationId,
        );
      } catch (error) {
        const savedLocationsResponse =
          await savedLocationsApi.getAllSavedLocations();
        setLocations(() =>
          savedLocationsResponse.items.sort((a, b) => {
            return a.location.businessName.localeCompare(
              b.location.businessName,
            );
          }),
        );
        setDefaultPickupLocationId(
          savedLocationsResponse.defaultPickupLocationId,
        );
        setDefaultDeliveryLocationId(
          savedLocationsResponse.defaultDeliveryLocationId,
        );
        throw new Error("Failed to delete the location: " + error);
      }
    }
  };

  const onToggleFavourite = async (
    savedLocationId: UUID,
    locations: SavedLocation[] | undefined,
    setLocations: (
      updateFn: (
        prev: SavedLocation[] | undefined,
      ) => SavedLocation[] | undefined,
    ) => void,
  ) => {
    const prevValue = locations?.find(
      (loc) => loc.savedLocationId === savedLocationId,
    )?.isFavourite;

    return handleLocationUpdate(
      savedLocationId,
      () => {
        setLocations((prevLocations) =>
          prevLocations?.map((loc) =>
            loc.savedLocationId === savedLocationId
              ? { ...loc, isFavourite: !loc.isFavourite }
              : loc,
          ),
        );
      },
      () => savedLocationsService.toggleIsFavourite(savedLocationId),
      () => savedLocationsService.getLocation(savedLocationId),
      () => {
        setLocations((prevLocations) =>
          prevLocations?.map((loc) =>
            loc.savedLocationId === savedLocationId
              ? { ...loc, isFavourite: prevValue ?? false }
              : loc,
          ),
        );
      },
      "Failed to mark location as favourite: ",
    );
  };

  const onToggleBranch = async (
    savedLocationId: UUID,
    locations: SavedLocation[] | undefined,
    setLocations: (
      updateFn: (
        prev: SavedLocation[] | undefined,
      ) => SavedLocation[] | undefined,
    ) => void,
  ) => {
    const prevValue = locations?.find(
      (loc) => loc.savedLocationId === savedLocationId,
    )?.isBranch;

    return handleLocationUpdate(
      savedLocationId,
      () => {
        setLocations((prevLocations) =>
          prevLocations?.map((loc) =>
            loc.savedLocationId === savedLocationId
              ? { ...loc, isBranch: !loc.isBranch }
              : loc,
          ),
        );
      },
      () => savedLocationsService.toggleIsBranch(savedLocationId),
      () => savedLocationsService.getLocation(savedLocationId),
      () => {
        setLocations((prevLocations) =>
          prevLocations?.map((loc) =>
            loc.savedLocationId === savedLocationId
              ? { ...loc, isBranch: prevValue ?? false }
              : loc,
          ),
        );
      },
      "Failed to mark location as branch: ",
    );
  };

  // Pickup and delivery defaults behave the same; only the endpoint and the
  // field in the refreshed list differ
  const onToggleDefaultLocation = async (
    kind: "pickup" | "delivery",
    savedLocationId: UUID,
    currentDefaultId: string | undefined,
    setDefaultId: (id: string | undefined) => void,
  ) => {
    setDefaultId(
      currentDefaultId === savedLocationId ? undefined : savedLocationId,
    );

    setLoadingLocations((prev) => ({ ...prev, [savedLocationId]: true }));
    try {
      try {
        await (kind === "pickup"
          ? savedLocationsService.toggleDefaultPickupLocation(savedLocationId)
          : savedLocationsService.toggleDefaultDeliveryLocation(
              savedLocationId,
            ));
      } catch (error) {
        // Put back the default the customer had before the optimistic flip
        setDefaultId(currentDefaultId);
        console.error(`Failed to change the default ${kind} location`, {
          error,
        });
        showDefaultLocationNotice(
          `We couldn't change your default ${kind} location. Please try again.`,
        );
        return;
      }
      // The change saved, so a failed refresh keeps the optimistic star: it
      // already shows the new default. Reverting it would invite a retry that
      // toggles the default back off on the server.
      try {
        const savedLocationsResponse =
          await savedLocationsApi.getAllSavedLocations();
        setDefaultId(
          kind === "pickup"
            ? savedLocationsResponse.defaultPickupLocationId
            : savedLocationsResponse.defaultDeliveryLocationId,
        );
      } catch (error) {
        console.warn("Failed to refresh the saved locations", { error });
      }
    } finally {
      setLoadingLocations((prev) => ({ ...prev, [savedLocationId]: false }));
    }
  };

  const onToggleDefaultPickupLocation = (
    savedLocationId: UUID,
    defaultPickupLocationId: string | undefined,
    setDefaultPickupLocationId: (id: string | undefined) => void,
  ) =>
    onToggleDefaultLocation(
      "pickup",
      savedLocationId,
      defaultPickupLocationId,
      setDefaultPickupLocationId,
    );

  const onToggleDefaultDeliveryLocation = (
    savedLocationId: UUID,
    defaultDeliveryLocationId: string | undefined,
    setDefaultDeliveryLocationId: (id: string | undefined) => void,
  ) =>
    onToggleDefaultLocation(
      "delivery",
      savedLocationId,
      defaultDeliveryLocationId,
      setDefaultDeliveryLocationId,
    );

  const dialogs = (
    <>
      {deleteConfirmDialogs}
      {defaultLocationNoticeDialogs}
    </>
  );

  return {
    loadingLocations,
    onModifyLocation,
    onDelete,
    onToggleFavourite,
    onToggleBranch,
    onToggleDefaultPickupLocation,
    onToggleDefaultDeliveryLocation,
    dialogs,
  };
};
